import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, Typography, CircularProgress, TextField, InputAdornment, Toolbar, alpha, Paper, Tooltip, IconButton } from '@mui/material';
import { CreateNewFolder as CreateNewFolderIcon, Search as SearchIcon, Delete as DeleteIcon } from '@mui/icons-material';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import api from '../../utils/api';
import UploadZone from './UploadZone';
import FileList from './FileList';
import FileBreadcrumbs from './FileBreadcrumbs';
import MoveDialog from './MoveDialog';

const MySwal = withReactContent(Swal);

// --- FUNZIONI HELPER (definite fuori dal componente per performance) ---

const processFiles = (files, currentPath) => {
    if (!files) return [];
    const fileMap = new Map();
    if (currentPath) {
        const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/', currentPath.length - 2) + 1);
        fileMap.set('..', { name: parentPath, isDir: true, isParent: true });
    }
    files.forEach(file => {
        const { name } = file;
        if (!name.startsWith(currentPath)) return;
        const pathWithoutPrefix = name.substring(currentPath.length);
        if (!pathWithoutPrefix || pathWithoutPrefix === '/') return;
        const parts = pathWithoutPrefix.split('/');
        if (pathWithoutPrefix.endsWith('/') && parts.length === 2) {
            const dirName = currentPath + parts[0] + '/';
            if (!fileMap.has(dirName)) fileMap.set(dirName, { name: dirName, isDir: true, lastModified: file.lastModified });
        } else if (parts.length > 1 && parts[0]) {
            const dirName = currentPath + parts[0] + '/';
            if (!fileMap.has(dirName)) fileMap.set(dirName, { name: dirName, isDir: true });
        } else if (pathWithoutPrefix && !pathWithoutPrefix.includes('/')) {
            fileMap.set(name, { ...file, isDir: false });
        }
    });
    return Array.from(fileMap.values()).sort((a, b) => {
        if (a.isParent) return -1; if (b.isParent) return 1;
        if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
        return a.name.localeCompare(b.name);
    });
};

const getNewNameForDuplicate = (originalName, existingFiles) => {
    const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.'));
    const extension = originalName.substring(originalName.lastIndexOf('.'));
    let counter = 1; let newName;
    while (true) {
        newName = `${nameWithoutExt} (${counter})${extension}`;
        if (!existingFiles.some(file => file.name === newName)) return newName;
        counter++;
    }
};

const getNewFolderNameForDuplicate = (originalName, existingFiles) => {
    const baseName = originalName.endsWith('/') ? originalName.slice(0, -1) : originalName;
    let counter = 1; let newName;
    while (true) {
        newName = `${baseName} (${counter})/`;
        const fullPath = originalName.substring(0, originalName.lastIndexOf('/') + 1) + newName;
        if (!existingFiles.some(file => file.name === fullPath)) return newName.slice(0, -1);
        counter++;
    }
};

// =================================================================
// COMPONENTE PRINCIPALE
// =================================================================
const CloudExplorer = ({ isAdminView = false, targetUserId = null }) => {
    // --- STATI ---
    const [userAllFiles, setUserAllFiles] = useState([]);
    const [currentPath, setCurrentPath] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selected, setSelected] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);

    // --- FUNZIONE HELPER INTERNA PER URL DINAMICI ---
    const getApiUrl = useCallback((action) => {
        if (isAdminView && targetUserId) {
            const base = `/admin/cloud/users/${targetUserId}`;
            switch (action) {
                case 'list': return `${base}/files`;
                case 'upload': return `${base}/files`;
                case 'createFolder': return `${base}/folders`;
                case 'rename': return `${base}/rename`;
                case 'delete': return `${base}/delete`;
                case 'move': return `${base}/move`;
                default: return '';
            }
        } else {
            switch (action) {
                case 'list': return '/cloud/files';
                case 'upload': return '/cloud/files';
                case 'createFolder': return '/cloud/folders';
                case 'renameFile': return '/cloud/files/rename';
                case 'renameFolder': return '/cloud/folders/rename';
                case 'deleteFiles': return '/cloud/files';
                case 'deleteFolder': return '/cloud/folders';
                case 'move': return '/cloud/files/move';
                case 'download': return '/cloud/files/download';
                default: return '';
            }
        }
    }, [isAdminView, targetUserId]);
    
    // --- FETCHING DATI ---
    const fetchAllUserFiles = useCallback(async () => {
        const url = getApiUrl('list');
        if (!url) return;
        setIsLoading(true);
        try {
            const response = await api.get(url);
            setUserAllFiles(response || []);
        } catch (error) {
            console.error("Errore nel recuperare i file:", error);
            MySwal.fire('Errore', 'Impossibile caricare i dati del cloud.', 'error');
        } finally { setIsLoading(false); }
    }, [getApiUrl]);

    useEffect(() => { fetchAllUserFiles() }, [fetchAllUserFiles]);
    const refreshAllData = () => fetchAllUserFiles();

    const allProcessedFiles = useMemo(() => processFiles(userAllFiles, ''), [userAllFiles]);

    // --- LOGICA DI VISUALIZZAZIONE E RICERCA ---
    const currentViewFiles = useMemo(() => processFiles(userAllFiles, currentPath), [userAllFiles, currentPath]);
    const isSearching = searchTerm.length > 0;
    const searchResults = useMemo(() => {
        if (!isSearching) return [];
        return userAllFiles
            .filter(file => !file.name.endsWith('/') && file.name.toLowerCase().includes(searchTerm.toLowerCase()))
            .map(file => ({ ...file, isSearchResult: true }));
    }, [userAllFiles, searchTerm, isSearching]);
    const filesToDisplay = isSearching ? searchResults : currentViewFiles;

    // --- LOGICA DI SELEZIONE ---
    const handleSelectAllClick = (event) => {
        if (event.target.checked) setSelected(filesToDisplay.filter(f => !f.isDir && !f.isParent).map(f => f.name));
        else setSelected([]);
    };
    const handleFileSelect = (name) => {
        const newSelected = selected.includes(name) ? selected.filter(item => item !== name) : [...selected, name];
        setSelected(newSelected);
    };
    useEffect(() => { setSelected([]) }, [currentPath, searchTerm]);


    // --- GESTORI DI AZIONI ---
    
    const handleUpload = async (acceptedFiles) => {
        for (const file of acceptedFiles) {
            const destinationPath = currentPath + file.name;
            const duplicate = userAllFiles.find(f => f.name === destinationPath && !f.isDir);
            let action = 'upload';
            let finalFileName = file.name;

            if (duplicate) {
                const result = await MySwal.fire({
                    title: 'File Esistente', html: `Un file chiamato <strong>${file.name}</strong> esiste già. Cosa vuoi fare?`,
                    icon: 'warning', showCancelButton: true, showDenyButton: true,
                    confirmButtonText: 'Sostituisci', denyButtonText: 'Mantieni entrambi', cancelButtonText: 'Annulla',
                });
                if (result.isConfirmed) action = 'replace';
                else if (result.isDenied) {
                    action = 'keep_both';
                    finalFileName = getNewNameForDuplicate(destinationPath, userAllFiles.map(f => f.name));
                } else action = 'cancel';
            }

            if (action === 'cancel') continue;

            const formData = new FormData();
            formData.append('file', new File([file], finalFileName, { type: file.type }));
            formData.append('path', currentPath);

            try {
                await api.post(getApiUrl('upload'), formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            } catch (error) {
                console.error(`Errore upload di ${file.name}:`, error);
                MySwal.fire('Errore', `Impossibile caricare ${file.name}`, 'error');
            }
        }
        refreshAllData();
    };

    const handleCreateFolder = async () => {
        const { value: folderName } = await MySwal.fire({
            title: 'Crea Nuova Cartella', input: 'text', inputLabel: 'Nome della cartella',
            inputPlaceholder: 'Es: Documenti', showCancelButton: true, confirmButtonText: 'Crea',
            inputValidator: (value) => {
                if (!value) return 'Il nome è obbligatorio!';
                if (/[/\\?%*:|"<>]/g.test(value)) return 'Il nome contiene caratteri non validi.';
            }
        });
        if (folderName) {
            let finalFolderName = folderName;
            const destinationPath = currentPath + folderName + '/';
            const duplicate = userAllFiles.find(f => f.name === destinationPath && f.isDir);
            if (duplicate) {
                const result = await MySwal.fire({
                    title: 'Cartella Esistente', html: `Una cartella chiamata <strong>"${folderName}"</strong> esiste già.`,
                    icon: 'warning', showCancelButton: true, confirmButtonText: 'Mantieni Entrambi', denyButtonText: 'Annulla',
                    showDenyButton: true, cancelButtonText: ' ', customClass: { cancelButton: 'swal2-cancel-hidden' }
                });
                if (result.isConfirmed) {
                    finalFolderName = getNewFolderNameForDuplicate(folderName + '/', userAllFiles.map(f => f.name));
                } else return;
            }
            try {
                const fullPath = currentPath + finalFolderName;
                await api.post(getApiUrl('createFolder'), { path: fullPath });
                MySwal.fire({ toast: true, position: 'top-end', timer: 3000, title: 'Successo!', text: `Cartella "${finalFolderName}" creata.`, showConfirmButton: false, icon: 'success', timerProgressBar: true });
                refreshAllData();
            } catch (error) {
                console.error("Errore creazione cartella:", error);
                MySwal.fire({ toast: true, position: 'top-end', timer: 4000, title: 'Errore', text: error.response?.data?.message || 'Impossibile creare la cartella.', showConfirmButton: false, icon: 'error', timerProgressBar: true });
            }
        }
    };
    
    const handleRename = async (path, isDir) => {
        const currentName = isDir ? path.split('/').slice(-2, -1)[0] : path.split('/').pop();
        const { value: newName } = await MySwal.fire({
            title: `Rinomina ${isDir ? 'Cartella' : 'File'}`, input: 'text', inputValue: currentName,
            showCancelButton: true, confirmButtonText: 'Rinomina',
            inputValidator: (value) => {
                if (!value) return 'Il nome è obbligatorio!';
                if (value === currentName) return 'Il nuovo nome deve essere diverso.';
                if (/[/\\?%*:|"<>]/g.test(value)) return 'Il nome contiene caratteri non validi.';
            }
        });

        if (newName && newName !== currentName) {
            try {
                if (isAdminView) {
                    await api.post(getApiUrl('rename'), { oldPath: path, newName, isDir });
                } else {
                    const apiUrl = isDir ? getApiUrl('renameFolder') : getApiUrl('renameFile');
                    const payload = isDir ? { oldPath: path, newName } : { oldRelativePath: path, newName };
                    await api.post(apiUrl, payload);
                }
                MySwal.fire({ toast: true, icon: 'success', title: 'Rinominato con successo', position: 'top-end', showConfirmButton: false, timer: 3000 });
                refreshAllData();
            } catch (error) {
                console.error("Errore rinomina:", error);
                MySwal.fire('Errore', error.response?.data?.message || 'Impossibile rinominare.', 'error');
            }
        }
    };
    
    const handleDelete = (keys, isDir) => {
        const keysArray = Array.isArray(keys) ? keys : [keys];
        const deletingFolders = keysArray.length === 1 && isDir;
        MySwal.fire({
            title: 'Sei sicuro?',
            html: deletingFolders ? `Stai per eliminare la cartella <strong>${keysArray[0].split('/').slice(-2, -1)[0]}</strong> e <strong>tutto</strong> il suo contenuto.` : `Stai per eliminare ${keysArray.length} elementi.`,
            icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Sì, elimina!', cancelButtonText: 'Annulla'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    if (isAdminView) {
                        await api.delete(getApiUrl('delete'), { data: { keys: keysArray, isDir: deletingFolders } });
                    } else {
                        const apiUrl = isDir ? getApiUrl('deleteFolder') : getApiUrl('deleteFiles');
                        const payload = isDir ? { path: keysArray[0] } : { keys: keysArray };
                        await api.delete(apiUrl, { data: payload });
                    }
                    MySwal.fire({ toast: true, icon: 'success', title: 'Eliminato con successo', position: 'top-end', showConfirmButton: false, timer: 3000 });
                    refreshAllData();
                } catch (error) {
                    console.error("Errore eliminazione:", error);
                    MySwal.fire('Errore', error.response?.data?.message || 'Impossibile eliminare.', 'error');
                }
            }
        });
    };

    const handleMove = async (relativeKeys, destinationPath) => {
        const isMovingToSubfolder = relativeKeys.some(key => key.endsWith('/') && destinationPath.startsWith(key));
        if (isMovingToSubfolder) {
            MySwal.fire('Azione non permessa', 'Non puoi spostare una cartella dentro se stessa.', 'error');
            return;
        }
        try {
            await api.post(getApiUrl('move'), { relativeKeys, destinationPath });
            MySwal.fire({ toast: true, icon: 'success', title: 'Spostamento completato!', position: 'top-end', showConfirmButton: false, timer: 3000 });
            refreshAllData();
        } catch (error) {
            MySwal.fire('Errore', 'Impossibile spostare gli elementi.', 'error');
        }
    };

    const handleDownload = async (relativePath) => {
        try {
            const url = getApiUrl('download');
            const response = await api.get(`${url}/${encodeURIComponent(relativePath)}`);
            window.open(response.url, '_blank');
        } catch (error) {
            console.error("Errore download:", error);
            MySwal.fire('Errore', 'Impossibile scaricare il file.', 'error');
        }
    };

    const handleDeleteMultiple = () => (selected.length > 0) && handleDelete(selected, false);
    const handleDeleteSingle = (key, isDir) => handleDelete([key], isDir);
    const handleMoveMultiple = () => (selected.length > 0) && setIsMoveDialogOpen(true);
    const onConfirmMoveDialog = (destination) => {
        setIsMoveDialogOpen(false);
        if (destination !== null) handleMove(selected, destination);
    };

    return (
        <Box sx={{ p: 3 }}>
            {isSearching ? (
                 <Box sx={{ mb: 2 }}><Typography variant="h6">Risultati per: "{searchTerm}"</Typography></Box>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <FileBreadcrumbs path={currentPath} onNavigate={setCurrentPath} />
                    <Button variant="contained" startIcon={<CreateNewFolderIcon />} onClick={handleCreateFolder}>Nuova Cartella</Button>
                </Box>
            )}
            
            {!isSearching && <UploadZone onUpload={handleUpload} />}

            <Paper sx={{ my: 2 }}>
                <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, ...(selected.length > 0 && { bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity) }) }}>
                    {selected.length > 0 ? (
                        <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1">{selected.length} selezionati</Typography>
                    ) : (
                        <TextField variant="standard" placeholder="Cerca file ricorsivamente..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ flex: '1 1 100%' }} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} />
                    )}
                    {selected.length > 0 && (
                        <>
                            <Tooltip title="Sposta selezionati"><IconButton onClick={handleMoveMultiple}><DriveFileMoveIcon /></IconButton></Tooltip>
                            <Tooltip title="Elimina selezionati"><IconButton onClick={handleDeleteMultiple}><DeleteIcon /></IconButton></Tooltip>
                        </>
                    )}
                </Toolbar>
            </Paper>
            
            {isLoading && userAllFiles.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <FileList
                    files={filesToDisplay}
                    selectedFiles={selected}
                    onFileSelect={handleFileSelect}
                    onSelectAll={handleSelectAllClick}
                    onNavigate={setCurrentPath}
                    onDownload={handleDownload}
                    onDelete={handleDeleteSingle}
                    onRename={handleRename}
                    isSearchView={isSearching}
                    onMove={handleMove}
                />
            )}
            <MoveDialog
                open={isMoveDialogOpen}
                filesToMove={selected}
                allFiles={allProcessedFiles} 
                onClose={() => setIsMoveDialogOpen(false)}
                onConfirmMove={onConfirmMoveDialog}
            />
        </Box>
    );
};

export default CloudExplorer;